import { useNavigationNotifications } from '../hooks/useNavigationNotifications';
import { NavigationCount, NavigationNotifications } from './NavigationNotifications';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import {
  LayoutDashboard,
  ShoppingCart,
  LogOut,
  Zap,
  ChevronRight,
  Plug,
  Settings,
  Users,
  Tag,
  ChevronDown,
  PanelLeftClose,
  PanelLeft,
  UserCog,
  ClipboardList,
  Sliders,
  Package,
  Truck,
  Shield,
  ShieldCheck,
  Ban,
  RotateCcw,
  Wallet,
  TrendingUp,
  Coins,
  Percent,
  Calculator,
  BookOpen,
  BarChart3,
  MessageSquare,
  Inbox,
  Sparkles,
  Palette,
  Paintbrush,
  Type,
  Navigation,
  PanelBottom,
  Monitor,
  Code2,
  FileText,
  ShoppingBag,
  ArrowLeft,
  Megaphone,
  GitBranch,
  Mail,
  Clock3,
  Maximize2,
  Minimize2,
  Film,
  CircleHelp,
} from 'lucide-react';
import { authApi, aiApi } from '../lib/api';
import { useState, useEffect, useRef } from 'react';
import { ThemeToggle } from './ui/ThemeToggle';
import { useFullscreen } from '../contexts/FullscreenContext';

// Sidebar navigation for "Logistyka" section - grouped with separators
const sidebarGroup1 = [
  { name: 'Zamówienia', href: '/orders', icon: ShoppingCart },
  { name: 'Magazyn', href: '/warehouse', icon: Package },
  { name: 'Reklamacje', href: '/complaints', icon: RotateCcw },
];
const sidebarGroup2 = [
  { name: 'Statusy zamówień', href: '/admin/statuses', icon: Tag },
  { name: 'Automatyzacje', href: '/automations', icon: Zap },
];

// Settings sub-navigation (under Ustawienia in sidebar)
const settingsSubNavigation = [
  { name: 'Ogólne', href: '/admin/settings/general', icon: Sliders },
  { name: 'Obsługa zamówień', href: '/admin/settings/orders', icon: ClipboardList },
  { name: 'Panel klienta', href: '/admin/settings/portal', icon: Users },
  { name: 'Reklamacje', href: '/admin/settings/returns', icon: RotateCcw },
  { name: 'Walidacja danych', href: '/admin/settings/validation', icon: ShieldCheck },
  { name: 'Czarna lista', href: '/admin/settings/blacklist', icon: Ban },
  { name: 'Powiadomienia email', href: '/admin/settings/email-templates', icon: Mail },
];

// Sidebar navigation for "Finanse" section — grouped with separators
const financeSidebarGroup1 = [
  { name: 'Panel Finansowy', href: '/finance', icon: BarChart3, exact: true },
  { name: 'Cashflow', href: '/finance/cashflow', icon: TrendingUp },
  { name: 'Kalkulator', href: '/finance/calculator', icon: Calculator },
];
const financeSidebarGroup2 = [
  { name: 'Koszty', href: '/finance/costs', icon: Coins },
  { name: 'Podatki', href: '/finance/taxes', icon: Percent },
  { name: 'Reguły', href: '/finance/rules', icon: BookOpen },
];
const financeSidebarGroup3 = [
  { name: 'Ustawienia', href: '/finance/settings', icon: Settings },
];

// Sidebar navigation for "Wiadomości" section
const messagesSidebarGroup1 = [
  { name: 'Skrzynka odbiorcza', href: '/messages', icon: Inbox, exact: true },
];
const messagesSidebarGroup2 = [
  { name: 'Baza wiedzy', href: '/messages/knowledge-base', icon: BookOpen },
];
const messagesSidebarGroup3 = [
  { name: 'Ustawienia', href: '/messages/settings', icon: Settings },
];

// Sidebar navigation for "Kreator" section — built dynamically from URL integrationId
function getKreatorSettingsSubNav(integrationId: string) {
  const base = `/kreator/${integrationId}`;
  return [
    { name: 'Sklep', href: `${base}/shop`, icon: ShoppingBag },
    { name: 'Wygląd stron', href: `${base}/pages`, icon: Monitor },
    { name: 'Kolory', href: `${base}/colors`, icon: Palette },
    { name: 'Typografia', href: `${base}/typography`, icon: Type },
    { name: 'Nawigacja', href: `${base}/navigation`, icon: Navigation },
    { name: 'Stopka', href: `${base}/footer`, icon: PanelBottom },
    { name: 'Import / Eksport', href: `${base}/import-export`, icon: FileText },
  ];
}

// Routes that belong to "Logistyka" (sidebar visible)
const zarzadzanieRoutes = ['/orders', '/warehouse', '/complaints', '/automations', '/admin/statuses', '/admin/settings'];

export function Layout() {
  const { user, tenant, logout } = useAuthStore();
  const notifications = useNavigationNotifications();
  const newComplaints = notifications.complaints.data?.pagination.total ?? 0;
  const unreadMessages = notifications.messages.data ?? 0;
  const navigate = useNavigate();
  const location = useLocation();
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [settingsExpanded, setSettingsExpanded] = useState(
    location.pathname.startsWith('/admin/settings')
  );
  const [kreatorSettingsExpanded, setKreatorSettingsExpanded] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [tokenBalance, setTokenBalance] = useState<{ balance: number; isUnlimited: boolean } | null>(null);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    setKreatorSettingsExpanded(
      /^\/kreator\/[^/]+\/(settings|shop|pages|colors|typography|navigation|footer|import-export)/.test(location.pathname),
    );
  }, [location.pathname]);
  // #R17 - w fullscreen sidebar ZAWSZE renderuje się jako collapsed,
  // niezależnie od user pref. Bez tego w-14 (CSS force) + padding/margins
  // z trybu rozwiniętego = ikony obcięte, ramki prostokątne pionowe.
  // Po wyjściu z fullscreen wraca do user pref (state nie modyfikowany).
  const effectiveCollapsed = sidebarCollapsed || isFullscreen;

  // Fetch token balance and refresh periodically + on custom event
  useEffect(() => {
    const fetchBalance = () => {
      aiApi.getBalance()
        .then(res => setTokenBalance(res.data.data || res.data))
        .catch(() => {});
    };
    fetchBalance();
    const interval = setInterval(fetchBalance, 60000);
    window.addEventListener('token-balance-changed', fetchBalance);
    return () => {
      clearInterval(interval);
      window.removeEventListener('token-balance-changed', fetchBalance);
    };
  }, []);

  // Close user dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setUserDropdownOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors
    }
    logout();
    navigate('/login');
  };

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    if (href === '/finance') return location.pathname === '/finance';
    return location.pathname.startsWith(href);
  };

  const isZarzadzanieActive = zarzadzanieRoutes.some(route => location.pathname.startsWith(route));
  const isFinanceActive = location.pathname.startsWith('/finance');
  const isMessagesActive = location.pathname.startsWith('/messages');
  const isMessagesInboxExact = location.pathname === '/messages';
  // Kreator: sidebar only visible inside a specific store (/kreator/:id/...)
  const kreatorMatch = location.pathname.match(/^\/kreator\/([^/]+)\//);
  const kreatorIntegrationId = kreatorMatch ? kreatorMatch[1] : null;
  const isKreatorActive = !!kreatorIntegrationId;
  const isMarketingActive = location.pathname.startsWith('/marketing');
  const isStudioActive = location.pathname.startsWith('/studio');
  const isStudioEditorActive = /^\/studio\/[^/]+/.test(location.pathname);
  const isAdminPanelActive = location.pathname.startsWith('/admin/panel');
  const showSidebar = isZarzadzanieActive || isFinanceActive || (isMessagesActive && !isMessagesInboxExact) || isKreatorActive;

  // #R7-7 - sliding indicator dla głównego nav (Dashboard / Logistyka /
  // Finanse / Wiadomości / Marketing / Kreator). Pojedynczy element
  // <span> z position:absolute mierzy się do aktywnego linka i animuje
  // left/width gdy user kliknie inny - "płynie" po pasku zamiast skokowo
  // znikać+pojawiać. Decyzja R7 2026-05-01.
  const navItemsRef = useRef<Map<string, HTMLAnchorElement | null>>(new Map());
  const [navIndicator, setNavIndicator] = useState<{ left: number; width: number; opacity: number }>({ left: 0, width: 0, opacity: 0 });
  const activeNavKey: string | null =
    isZarzadzanieActive ? 'logistyka' :
    isFinanceActive ? 'finanse' :
    isMessagesActive ? 'messages' :
    isMarketingActive ? 'marketing' :
    isStudioActive ? 'studio' :
    location.pathname.startsWith('/kreator') ? 'kreator' :
    ((isActive('/') || location.pathname.startsWith('/dashboard')) && !isAdminPanelActive) ? 'dashboard' :
    null;
  useEffect(() => {
    if (!activeNavKey) {
      setNavIndicator(s => ({ ...s, opacity: 0 }));
      return;
    }
    // Dwa rAF żeby DOM zdążył się zaktualizować po nav change
    // (gdy user kliknie inny link, layout reflow musi się skończyć).
    const raf = requestAnimationFrame(() => {
      const el = navItemsRef.current.get(activeNavKey);
      if (!el || !el.parentElement) return;
      const itemRect = el.getBoundingClientRect();
      const navRect = el.parentElement.getBoundingClientRect();
      setNavIndicator({
        left: itemRect.left - navRect.left,
        width: itemRect.width,
        opacity: 1,
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [activeNavKey, location.pathname, newComplaints, unreadMessages]);

  return (
    <div className="min-h-screen bg-surface-base">
      {/* Top Navigation Bar */}
      {!isStudioEditorActive && (
      <header className="app-topnav fixed top-0 left-0 right-0 z-50 h-20 bg-surface-raised/80 backdrop-blur-xl border-b border-border-subtle transition-transform duration-slow ease-smooth">
        <div className="flex items-center h-full px-4 gap-4">
          {/* Logo — shrink-0, nav klei sie do logo (wyrownanie do lewej) */}
          <div className="shrink-0 flex items-center min-w-0">
          <Link to="/" className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="570 478 320 72" style={{ height: '32px', width: 'auto' }}>
              <defs><style>{`.st1{fill:#67e64c}.st2{fill:var(--logo-ecom-fill,#fff)}`}</style></defs>
              <g>
                <path d="M592.76 526.57c-.06.55.07 1.11.39 1.58l11.33 16.84 10.7-18.42z" style={{fill:'#259950'}}/>
                <path d="M574.85 500.7c-.06.55.07 1.11.39 1.58l11.33 16.84 10.7-18.42z" style={{fill:'#006aee'}}/>
                <path d="m574.84 500.76 8.88-18.25c.4-.82 1.23-1.34 2.14-1.34h49.36l-7.36 15.13a7.95 7.95 0 0 1-7.14 4.47h-45.88Z" style={{fill:'#44a6ff'}}/>
                <path d="m592.75 526.63 8.88-18.25c.4-.82 1.23-1.34 2.14-1.34h29.73l-7.36 15.13a7.95 7.95 0 0 1-7.14 4.47h-26.26Z" className="st1"/>
                <path d="M677.3 521.43v5.54h-23.12v-29.85h22.56v5.54h-15.69v6.48h13.86v5.37h-13.86v6.91h16.25ZM680.46 515.5c0-6.91 5.33-11.81 12.79-11.81 4.82 0 8.62 2.09 10.28 5.84l-5.16 2.77c-1.24-2.18-3.07-3.16-5.16-3.16-3.37 0-6.01 2.35-6.01 6.35s2.64 6.35 6.01 6.35c2.09 0 3.92-.94 5.16-3.16l5.16 2.81c-1.66 3.67-5.46 5.8-10.28 5.8-7.46 0-12.79-4.9-12.79-11.81ZM705.36 515.5c0-6.91 5.33-11.81 12.62-11.81s12.58 4.9 12.58 11.81-5.29 11.81-12.58 11.81-12.62-4.9-12.62-11.81m18.47 0c0-3.97-2.52-6.35-5.84-6.35s-5.89 2.39-5.89 6.35 2.56 6.35 5.89 6.35 5.84-2.39 5.84-6.35M773.76 513.84v13.14h-6.65v-12.11c0-3.71-1.66-5.42-4.35-5.42-2.99 0-5.08 1.92-5.08 6.01v11.51h-6.65v-12.11c0-3.71-1.58-5.42-4.35-5.42-2.94 0-5.03 1.92-5.03 6.01v11.51H735v-22.94h6.35v2.64c1.71-1.96 4.26-2.99 7.21-2.99 3.2 0 5.93 1.24 7.55 3.75 1.83-2.35 4.82-3.75 8.27-3.75 5.46 0 9.38 3.16 9.38 10.15Z" className="st2"/>
                <path d="M787.1 502.66v7.89h13.82v1.28c0 2.35-1.91 4.26-4.26 4.26h-9.55v10.88h-6.91v-29.85h22.56v1.16a4.39 4.39 0 0 1-4.39 4.39h-11.26ZM806.97 497.12h6.65v25.6c0 2.35-1.91 4.26-4.26 4.26h-2.4v-29.85ZM818.1 515.5c0-6.91 5.33-11.81 12.62-11.81s12.58 4.9 12.58 11.81-5.29 11.81-12.58 11.81-12.62-4.9-12.62-11.81m18.47 0c0-3.97-2.52-6.35-5.84-6.35s-5.88 2.39-5.88 6.35 2.56 6.35 5.88 6.35 5.84-2.39 5.84-6.35M883.95 495.71l-7.64 29.4a2.82 2.82 0 0 1-2.65 1.86h-3.42c-.6 0-1.13-.37-1.34-.94l-4.82-13.31-4.99 13.32c-.21.55-.74.92-1.33.92h-3.43c-1.19 0-2.25-.75-2.66-1.87l-7.6-21.08h3.25c1.81 0 3.42 1.14 4.01 2.85l4.42 12.67 5.29-14.59c.2-.56.74-.94 1.33-.94H866c.6 0 1.14.38 1.34.95l5.13 14.58 4.57-21.02a4.26 4.26 0 0 1 4.01-2.82h2.89Z" className="st1"/>
              </g>
            </svg>
          </Link>
          </div>

          {/* Main nav links — wyrownane do lewej, kleja sie do logo. #R7-7
              sliding indicator: pojedyncze <span> absolute pozycjonowane,
              animuje left/width do aktywnego linka. */}
          <nav className="flex items-center gap-1 shrink-0 relative">
            {/* Sliding indicator (background highlight aktywnego itemu).
                #R13-2 - transition jako inline style zamiast Tailwind arbitrary.
                Tailwind 'transition-[left,width,opacity]' bywa kapryśny w
                JIT (czasem nie generuje rule). Inline style = pewność, że
                tylko left/width/opacity są w transition - border-color i
                box-shadow zostawione na sucho dla animacji nav-glow shimmer. */}
            <span
              aria-hidden
              className="absolute top-1/2 -translate-y-1/2 h-9 rounded-lg bg-surface-hover/60 border nav-glow pointer-events-none"
              style={{
                left: navIndicator.left,
                width: navIndicator.width,
                opacity: navIndicator.opacity,
                transition: 'left 400ms cubic-bezier(0.4, 0, 0.2, 1), width 400ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-out',
              }}
            />
            <Link
              ref={el => { navItemsRef.current.set('dashboard', el); }}
              to="/"
              className={`relative z-10 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors duration-normal ${
                activeNavKey === 'dashboard'
                  ? 'text-text-primary'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
              }`}
            >
              <LayoutDashboard className={`w-4 h-4 transition-colors duration-normal ${activeNavKey === 'dashboard' ? 'text-accent-primary' : ''}`} />
              Dashboard
            </Link>
            <Link
              ref={el => { navItemsRef.current.set('logistyka', el); }}
              to="/orders"
              className={`relative z-10 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors duration-normal ${
                activeNavKey === 'logistyka'
                  ? 'text-text-primary'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
              }`}
            >
              <Truck className={`w-4 h-4 transition-colors duration-normal ${activeNavKey === 'logistyka' ? 'text-accent-primary' : ''}`} />
              Logistyka
              <NavigationCount count={newComplaints} label="Nowe zgłoszenia" />
            </Link>
            <Link
              ref={el => { navItemsRef.current.set('finanse', el); }}
              to="/finance"
              className={`relative z-10 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors duration-normal ${
                activeNavKey === 'finanse'
                  ? 'text-text-primary'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
              }`}
            >
              <Wallet className={`w-4 h-4 transition-colors duration-normal ${activeNavKey === 'finanse' ? 'text-accent-primary' : ''}`} />
              Finanse
            </Link>
            <Link
              ref={el => { navItemsRef.current.set('messages', el); }}
              to="/messages"
              className={`relative z-10 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors duration-normal ${
                activeNavKey === 'messages'
                  ? 'text-text-primary'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
              }`}
            >
              <MessageSquare className={`w-4 h-4 transition-colors duration-normal ${activeNavKey === 'messages' ? 'text-accent-primary' : ''}`} />
              Wiadomości
              <NavigationCount count={unreadMessages} label="Nieprzeczytane wiadomości" />
            </Link>
            <Link
              ref={el => { navItemsRef.current.set('marketing', el); }}
              to="/marketing"
              className={`relative z-10 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors duration-normal ${
                activeNavKey === 'marketing'
                  ? 'text-text-primary'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
              }`}
            >
              <Megaphone className={`w-4 h-4 transition-colors duration-normal ${activeNavKey === 'marketing' ? 'text-accent-primary' : ''}`} />
              Marketing
            </Link>
            <Link
              ref={el => { navItemsRef.current.set('kreator', el); }}
              to="/kreator"
              className={`relative z-10 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors duration-normal ${
                activeNavKey === 'kreator'
                  ? 'text-text-primary'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
              }`}
            >
              <Paintbrush className={`w-4 h-4 transition-colors duration-normal ${activeNavKey === 'kreator' ? 'text-accent-primary' : ''}`} />
              Kreator
            </Link>
            <Link
              ref={el => { navItemsRef.current.set('studio', el); }}
              to="/studio"
              className={`relative z-10 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors duration-normal ${
                activeNavKey === 'studio'
                  ? 'text-text-primary'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
              }`}
            >
              <Film className={`w-4 h-4 transition-colors duration-normal ${activeNavKey === 'studio' ? 'text-accent-primary' : ''}`} />
              Studio
            </Link>

          </nav>

          {/* Right side: Bell + User dropdown — flex-1 justify-end (balansuje z logo) */}
          <div className="flex-1 flex items-stretch gap-3 justify-end">
            <NavigationNotifications query={notifications.complaints} />

            {/* User dropdown */}
            <div className="relative" ref={userDropdownRef}>
              <button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-hover/30 border border-border-subtle hover:border-accent-primary/30 transition-all"
              >
                <div className="w-7 h-7 rounded-lg bg-accent-primary flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-xs">
                    {user?.firstName?.[0] || user?.email[0].toUpperCase()}
                  </span>
                </div>
                <div className="text-left hidden sm:block">
                  <p className="text-sm font-medium text-text-primary leading-tight">
                    {user?.firstName || user?.email}
                  </p>
                  <p className="text-[10px] text-text-muted leading-tight">{tenant?.name}</p>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-text-secondary transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown menu */}
              {userDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-surface-raised border border-border rounded-xl shadow-2xl overflow-hidden z-50">
                  {/* User info header */}
                  <div className="px-4 py-3 border-b border-border-subtle">
                    <p className="text-sm font-medium text-text-primary">{user?.firstName || user?.email}</p>
                    <p className="text-xs text-text-muted">{user?.email}</p>
                  </div>

                  {/* Token balance */}
                  {tokenBalance && (
                    <div className="px-4 py-2 border-b border-border-subtle flex items-center gap-2">
                      <Coins className="w-3.5 h-3.5 text-accent-primary" />
                      <span className="text-xs text-text-secondary">Tokeny:</span>
                      {tokenBalance.isUnlimited ? (
                        <span className="text-xs text-success font-medium">Nieograniczone</span>
                      ) : (
                        <span className={`text-xs font-medium ${tokenBalance.balance < 1000 ? 'text-danger' : 'text-text-primary'}`}>
                          {tokenBalance.balance.toLocaleString('pl-PL')}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="py-1">
                    <Link
                      to="/account"
                      onClick={() => setUserDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-colors"
                    >
                      <UserCog className="w-4 h-4" />
                      Moje konto
                    </Link>
                    <Link
                      to="/integrations"
                      onClick={() => setUserDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-colors"
                    >
                      <Plug className="w-4 h-4" />
                      Integracje
                    </Link>
                    <Link
                      to="/admin/users"
                      onClick={() => setUserDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-colors"
                    >
                      <Users className="w-4 h-4" />
                      Użytkownicy
                    </Link>
                    <Link
                      to="/ai"
                      onClick={() => setUserDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-colors"
                    >
                      <Sparkles className="w-4 h-4" />
                      Sztuczna inteligencja
                    </Link>
                    {user?.role === 'SUPER_ADMIN' && (
                      <Link
                        to="/admin/panel"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-colors"
                      >
                        <Shield className="w-4 h-4" />
                        Panel admina
                      </Link>
                    )}
                  </div>

                  {/* Theme toggle: 2 ikony równo w dwóch kolumnach */}
                  <div className="border-t border-border-subtle px-4 py-3">
                    <ThemeToggle size="sm" fullWidth />
                  </div>

                  {/* Wyloguj: pełnowymiarowy button na dole */}
                  <div className="border-t border-border-subtle px-4 py-3">
                    <button
                      onClick={() => {
                        setUserDropdownOpen(false);
                        handleLogout();
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-danger border border-danger/30 bg-danger/10 hover:bg-danger/20 hover:border-danger/50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Wyloguj
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      )}

      {/* Sidebar */}
      {showSidebar && (
        <aside
          className={`fixed top-20 bottom-0 left-0 bg-surface-raised/30 backdrop-blur-xl border-r border-border-subtle z-40 transition-all duration-300 flex flex-col ${
            effectiveCollapsed ? 'w-14' : 'w-56'
          }`}
        >
          {/* Navigation */}
          <nav className={`flex-1 overflow-y-auto pt-4 pb-2 ${effectiveCollapsed ? 'px-1.5' : 'px-3'}`}>
            {isZarzadzanieActive && (
              <>
                {/* Group 1: Zamówienia, Magazyn, Reklamacje */}
                <div className="space-y-1.5">
                  {sidebarGroup1.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                        isActive(item.href)
                          ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                          : 'text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle'
                      } ${effectiveCollapsed ? 'justify-center !px-2' : ''}`}
                      title={effectiveCollapsed ? item.name : undefined}
                    >
                      <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive(item.href) ? 'text-accent-primary' : ''}`} />
                      {!effectiveCollapsed && (
                        <>
                          <span className="flex-1">{item.name}</span>
                          {isActive(item.href) && (
                            <ChevronRight className="w-3.5 h-3.5 text-accent-primary" />
                          )}
                        </>
                      )}
                    </Link>
                  ))}
                </div>

                {/* Separator + Group 2: Statusy zamówień, Automatyzacje */}
                <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                  {sidebarGroup2.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                        isActive(item.href)
                          ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                          : 'text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle'
                      } ${effectiveCollapsed ? 'justify-center !px-2' : ''}`}
                      title={effectiveCollapsed ? item.name : undefined}
                    >
                      <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive(item.href) ? 'text-accent-primary' : ''}`} />
                      {!effectiveCollapsed && (
                        <>
                          <span className="flex-1">{item.name}</span>
                          {isActive(item.href) && (
                            <ChevronRight className="w-3.5 h-3.5 text-accent-primary" />
                          )}
                        </>
                      )}
                    </Link>
                  ))}
                </div>

                {/* Separator + Settings */}
                <div className="mt-3 pt-3 border-t border-border">
                  {effectiveCollapsed ? (
                    <Link
                      to="/admin/settings"
                      className={`flex items-center justify-center px-2 py-2.5 rounded-lg text-sm transition-all ${
                        isActive('/admin/settings')
                          ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                          : 'text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle'
                      }`}
                      title="Ustawienia"
                    >
                      <Settings className={`w-4 h-4 ${isActive('/admin/settings') ? 'text-accent-primary' : ''}`} />
                    </Link>
                  ) : (
                    <>
                      <button
                        onClick={() => setSettingsExpanded(!settingsExpanded)}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 w-full text-left"
                      >
                        <Settings className="w-4 h-4 flex-shrink-0" />
                        <span className="flex-1 text-left">Ustawienia</span>
                        <ChevronDown
                          className={`w-3.5 h-3.5 transition-transform ${
                            settingsExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {settingsExpanded && (
                        <div className="mt-1 space-y-1 pl-3">
                          {settingsSubNavigation.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all ${
                                isActive(item.href)
                                  ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                                  : 'text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle'
                              }`}
                            >
                              <item.icon className={`w-3.5 h-3.5 flex-shrink-0 ${isActive(item.href) ? 'text-accent-primary' : ''}`} />
                              <span className="flex-1">{item.name}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {isFinanceActive && (
              <>
                {[financeSidebarGroup1, financeSidebarGroup2, financeSidebarGroup3].map((group, gi) => (
                  <div key={gi} className={`space-y-1.5 ${gi > 0 ? 'mt-3 pt-3 border-t border-border' : ''}`}>
                    {group.map((item) => {
                      const exact = 'exact' in item && item.exact;
                      const active = exact
                        ? location.pathname === item.href
                        : location.pathname.startsWith(item.href);
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                            active
                              ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                              : 'text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle'
                          } ${effectiveCollapsed ? 'justify-center !px-2' : ''}`}
                          title={effectiveCollapsed ? item.name : undefined}
                        >
                          <item.icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-accent-primary' : ''}`} />
                          {!effectiveCollapsed && (
                            <>
                              <span className="flex-1">{item.name}</span>
                              {active && (
                                <ChevronRight className="w-3.5 h-3.5 text-accent-primary" />
                              )}
                            </>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </>
            )}

            {isMessagesActive && (
              <>
                {[messagesSidebarGroup1, messagesSidebarGroup2, messagesSidebarGroup3].map((group, gi) => (
                  <div key={gi} className={`space-y-1.5 ${gi > 0 ? 'mt-3 pt-3 border-t border-border' : ''}`}>
                    {group.map((item) => {
                      const exact = 'exact' in item && item.exact;
                      const active = exact
                        ? location.pathname === item.href
                        : location.pathname.startsWith(item.href);
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                            active
                              ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                              : 'text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle'
                          } ${effectiveCollapsed ? 'justify-center !px-2' : ''}`}
                          title={effectiveCollapsed ? item.name : undefined}
                        >
                          <item.icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-accent-primary' : ''}`} />
                          {!effectiveCollapsed && (
                            <>
                              <span className="flex-1">{item.name}</span>
                              {active && (
                                <ChevronRight className="w-3.5 h-3.5 text-accent-primary" />
                              )}
                            </>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </>
            )}

            {isKreatorActive && kreatorIntegrationId && (() => {
              const kreatorSubNav = getKreatorSettingsSubNav(kreatorIntegrationId);
              const kreatorSettingsActive = kreatorSubNav.some(i => location.pathname.startsWith(i.href));
              const builderHref = `/kreator/${kreatorIntegrationId}/builder/pages`;
              const builderActive = location.pathname.startsWith(`/kreator/${kreatorIntegrationId}/builder`);
              const advertorialsHref = `/kreator/${kreatorIntegrationId}/advertorials`;
              const advertorialsActive = location.pathname.startsWith(advertorialsHref);
              const quizzesHref = `/kreator/${kreatorIntegrationId}/quizzes`;
              const quizzesActive = location.pathname.startsWith(quizzesHref);
              const productsHref = `/kreator/${kreatorIntegrationId}/products`;
              const productsActive = location.pathname.startsWith(productsHref);
              const funnelsHref = `/kreator/${kreatorIntegrationId}/funnels`;
              const funnelsActive = location.pathname.startsWith(funnelsHref);
              const bundlesHref = `/kreator/${kreatorIntegrationId}/bundles`;
              const bundlesActive = location.pathname.startsWith(bundlesHref);
              const countersHref = `/kreator/${kreatorIntegrationId}/counters`;
              const countersActive = location.pathname.startsWith(countersHref);
              const codeEditorHref = `/kreator/${kreatorIntegrationId}/code-editor`;
              const codeEditorActive = location.pathname.startsWith(codeEditorHref);

              const topLinksGroup1 = [
                { name: 'Lejki', href: funnelsHref, icon: GitBranch, active: funnelsActive },
                { name: 'Zestawy', href: bundlesHref, icon: Package, active: bundlesActive },
                { name: 'Liczniki', href: countersHref, icon: Clock3, active: countersActive },
              ];
              const topLinksGroup2 = [
                { name: 'Strony', href: builderHref, icon: LayoutDashboard, active: builderActive },
                { name: 'Advertoriale', href: advertorialsHref, icon: FileText, active: advertorialsActive },
                { name: 'Quizy', href: quizzesHref, icon: CircleHelp, active: quizzesActive },
                { name: 'Produkty', href: productsHref, icon: ShoppingBag, active: productsActive },
              ];
              const renderTopLink = (item: { name: string; href: string; icon: typeof GitBranch; active: boolean }) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                    item.active
                      ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                      : 'text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle'
                  } ${effectiveCollapsed ? 'justify-center !px-2' : ''}`}
                  title={effectiveCollapsed ? item.name : undefined}
                >
                  <item.icon className={`w-4 h-4 flex-shrink-0 ${item.active ? 'text-accent-primary' : ''}`} />
                  {!effectiveCollapsed && (
                    <>
                      <span className="flex-1">{item.name}</span>
                      {item.active && <ChevronRight className="w-3.5 h-3.5 text-accent-primary" />}
                    </>
                  )}
                </Link>
              );

              return (
                <div>
                  {/* Back to store selection */}
                  {!effectiveCollapsed ? (
                    <Link
                      to="/kreator"
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-all border border-border-subtle hover:border-accent-primary/20"
                    >
                      <ArrowLeft className="w-4 h-4 flex-shrink-0" />
                      <span className="flex-1 truncate">Sklepy</span>
                    </Link>
                  ) : (
                    <Link
                      to="/kreator"
                      className="flex items-center justify-center px-2 py-2.5 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-all border border-border-subtle hover:border-accent-primary/20"
                      title="Sklepy"
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </Link>
                  )}

                  {/* Top-level group 1: Lejki, Zestawy */}
                  <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                    {topLinksGroup1.map(renderTopLink)}
                  </div>

                  {/* Separator + group 2: Strony, Advertoriale, Produkty */}
                  <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                    {topLinksGroup2.map(renderTopLink)}
                  </div>

                  {/* Separator + Edytor kodu + Ustawienia */}
                  <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                  {/* Edytor kodu — above Ustawienia */}
                  {effectiveCollapsed ? (
                    <Link
                      to={codeEditorHref}
                      className={`flex items-center justify-center px-2 py-2.5 rounded-lg text-sm transition-all ${
                        codeEditorActive
                          ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                          : 'text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle'
                      }`}
                      title="Edytor kodu"
                    >
                      <Code2 className={`w-4 h-4 ${codeEditorActive ? 'text-accent-primary' : ''}`} />
                    </Link>
                  ) : (
                    <Link
                      to={codeEditorHref}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                        codeEditorActive
                          ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                          : 'text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle'
                      }`}
                    >
                      <Code2 className={`w-4 h-4 flex-shrink-0 ${codeEditorActive ? 'text-accent-primary' : ''}`} />
                      <span className="flex-1">Edytor kodu</span>
                      {codeEditorActive && <ChevronRight className="w-3.5 h-3.5 text-accent-primary" />}
                    </Link>
                  )}

                  {/* Ustawienia */}
                  {effectiveCollapsed ? (
                    <Link
                      to={kreatorSubNav[0].href}
                      className={`flex items-center justify-center px-2 py-2.5 rounded-lg text-sm transition-all ${
                        kreatorSettingsActive
                          ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                          : 'text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle'
                      }`}
                      title="Ustawienia"
                    >
                      <Settings className={`w-4 h-4 ${kreatorSettingsActive ? 'text-accent-primary' : ''}`} />
                    </Link>
                  ) : (
                    <>
                      <button
                        onClick={() => setKreatorSettingsExpanded(!kreatorSettingsExpanded)}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 w-full text-left"
                      >
                        <Settings className="w-4 h-4 flex-shrink-0" />
                        <span className="flex-1 text-left">Ustawienia</span>
                        <ChevronDown
                          className={`w-3.5 h-3.5 transition-transform ${
                            kreatorSettingsExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {kreatorSettingsExpanded && (
                        <div className="mt-1 space-y-1 pl-3">
                          {kreatorSubNav.map((item) => {
                            const active = location.pathname.startsWith(item.href);
                            return (
                              <Link
                                key={item.name}
                                to={item.href}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all ${
                                  active
                                    ? 'text-text-primary bg-accent-primary/15 border border-accent-primary/30'
                                    : 'text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle'
                                }`}
                              >
                                <item.icon className={`w-3.5 h-3.5 flex-shrink-0 ${active ? 'text-accent-primary' : ''}`} />
                                <span className="flex-1">{item.name}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                  </div>
                </div>
              );
            })()}
          </nav>

          {/* Bottom: collapse toggle + fullscreen toggle (split 50/50 gdy
              menu rozwinięte; w stanie fullscreen tylko przycisk wyjścia z fs;
              gdy menu zwinięte poza fullscreen-em - dwie ikonki w pionie). */}
          <div className={`border-t border-border py-2 ${effectiveCollapsed ? 'px-1.5' : 'px-3'}`}>
            {isFullscreen ? (
              // W fullscreen tylko jeden przycisk - wyjście. Cmd+Shift+F też wyjdzie.
              <button
                onClick={() => { void toggleFullscreen(); }}
                className="flex items-center justify-center w-full px-3 py-2.5 rounded-lg text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 cursor-pointer"
                title="Wyjdź z trybu pełnoekranowego (Cmd+Shift+F)"
              >
                <Minimize2 className="w-[15px] h-[15px] flex-shrink-0" />
              </button>
            ) : effectiveCollapsed ? (
              // Menu zwinięte poza fullscreen - dwie ikony w pionie.
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => setSidebarCollapsed(false)}
                  className="flex items-center justify-center px-2 py-2 rounded-lg text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 cursor-pointer"
                  title="Rozwiń menu"
                >
                  <PanelLeft className="w-[15px] h-[15px]" />
                </button>
                <button
                  onClick={() => { void toggleFullscreen(); }}
                  className="flex items-center justify-center px-2 py-2 rounded-lg text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 cursor-pointer"
                  title="Tryb pełnoekranowy (Cmd+Shift+F)"
                >
                  <Maximize2 className="w-[15px] h-[15px]" />
                </button>
              </div>
            ) : (
              // Menu rozwinięte - split 50/50 w jednym wierszu.
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  onClick={() => setSidebarCollapsed(true)}
                  className="flex items-center justify-center gap-2 px-2 py-2.5 rounded-lg text-[11px] uppercase tracking-wider text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 cursor-pointer"
                  title="Zwiń menu"
                >
                  <PanelLeftClose className="w-[15px] h-[15px] flex-shrink-0" />
                  <span>Zwiń</span>
                </button>
                <button
                  onClick={() => { void toggleFullscreen(); }}
                  className="flex items-center justify-center gap-2 px-2 py-2.5 rounded-lg text-[11px] uppercase tracking-wider text-text-secondary border border-transparent hover:text-text-primary hover:bg-surface-hover/50 hover:border-border-subtle transition-all duration-200 cursor-pointer"
                  title="Tryb pełnoekranowy (Cmd+Shift+F)"
                >
                  <Maximize2 className="w-[15px] h-[15px] flex-shrink-0" />
                  <span>Ekran</span>
                </button>
              </div>
            )}
          </div>
        </aside>
      )}

      {/* #R14-3 - floating fullscreen button USUNIĘTY. User zdecydował, że
          na stronach bez sidebara (Dashboard, Konto, Integracje, Kreator-
          przed-wyborem-sklepu) fullscreen ma być dostępny TYLKO przez
          skrót Cmd+Shift+F. Strony z sidebarem mają button w split 50/50
          na dole. Inbox ma własny przycisk w swoim sidebarze. */}

      {/* Main content */}
      <div
        className={`transition-all duration-300 ${isStudioEditorActive ? 'pt-0' : 'pt-20'} ${
          showSidebar ? (effectiveCollapsed ? 'ml-14' : 'ml-56') : ''
        }`}
      >
        {/* Page content */}
        <main className={isStudioEditorActive ? 'p-0' : 'p-8'}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
